import React, { useState } from 'react';

import { parseIsoDate } from 'Helpers/dateFormat';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import ContentList from 'CommonComponents/ContentList';
import ContentListItem from 'CommonComponents/ContentListItem';
import DatePickerField from 'CommonComponents/DatePickerField';
import SectionTile from 'CommonComponents/SectionTile';
import TextButton from 'CommonComponents/TextButton';

import { useToast } from 'Hooks/useToast';

/**
 * The project's time lock date. Days on or before it are read-only on every
 * person's My Time Record, so this is how a period is closed once time has been
 * checked.
 *
 * It has its own Save and its own endpoint rather than joining the Settings
 * card's payload: only somebody holding manage-settings on the project may
 * close a period, and that endpoint is where the check lives.
 */
export default function TimeLockCard({ lockedUntil, updateUrl }) {
    const [value, setValue] = useState(lockedUntil || '');
    const [saving, setSaving] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [error, setError] = useState(null);

    const toast = useToast();

    const save = (next) => {
        setSaving(true);
        setError(null);

        axios
            .put(updateUrl, { time_locked_until: next === '' ? null : next })
            .then(({ data }) => {
                setValue(data.data?.time_locked_until || '');
                toast.success(next === '' ? $t('Time entries were reopened') : $t('Time entries were locked'));
            })
            .catch((e) => {
                const message = e.response?.status === 403 ? $t('You do not have permission to change the time lock date') : null;
                setError(message || e.response?.data?.message || $t('Something went wrong'));
            })
            .finally(() => {
                setSaving(false);
            });
    };

    return (
        <SectionTile collapsed={collapsed} header={$t('Time keeping')} onToggle={() => setCollapsed((previous) => !previous)}>
            <ContentList>
                <ContentListItem
                    title={$t('Time entries locked up to and including')}
                    info={$t('Days on or before this date become read-only for everyone recording time on this project. Leave it empty to lock nothing.')}
                >
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                        <DatePickerField
                            name="timeLockedUntil"
                            className={'tw-border-0 text-input'}
                            // Parsed from parts: new Date('2026-08-12') is UTC
                            // midnight and shows the previous day west of UTC.
                            value={parseIsoDate(value)}
                            onChange={(name, next) => setValue(next)}
                            valueFormat={'yyyy-MM-dd'}
                            disabled={saving}
                        />
                        {!!value && (
                            <TextButton variant="danger" onClick={() => save('')} disabled={saving}>
                                {$t('Clear')}
                            </TextButton>
                        )}
                    </div>
                </ContentListItem>
            </ContentList>

            {!!error && <Alert type="error">{error}</Alert>}

            <div className="tw-self-end tw-mt-auto">
                <Button variant="primary" onClick={() => save(value)} disabled={saving}>
                    {$t('Save')}
                </Button>
            </div>
        </SectionTile>
    );
}
